import 'zone.js';
import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';

import { environment } from '@environments/environment';


if (environment.production) {
    enableProdMode();
}

import { init as plausibleInit } from '@plausible-analytics/tracker';
import * as Sentry from "@sentry/angular";
import { CookieService } from '@shared/services/cookie.service';
import { BrowserCookieService } from '@core/services/browser-cookie.service';
import { CoreModule } from '@core/core.module';
import { CoreComponent } from './app/core/core.component';
import { bootstrapApplication } from '@angular/platform-browser';
import { APP_ORIGINS, hostOf, originFor, surfaceForHost } from '@core/config/app-surface';
import { dropLegacyHostCookie } from '@core/config/legacy-cookie';
import { setCanonicalHref } from '@core/config/canonical-link';

// Fix for "Unable to preventDefault inside passive event listener invocation" with CDK drag-drop
(function() {
    const supportsPassive = (() => {
        let passive = false;
        try {
            const opts = Object.defineProperty({}, 'passive', {
                get: () => passive = true
            });
            window.addEventListener('test', null as any, opts);
            window.removeEventListener('test', null as any, opts);
        } catch (e) {}
        return passive;
    })();

    if (supportsPassive) {
        const addEvent = EventTarget.prototype.addEventListener;
        const overwriteAddEvent = function(type: string, listener: any, options?: any) {
            const newOptions = options === true || (options && options.passive) ? options : false;
            return addEvent.call(this, type, listener, newOptions);
        };
        EventTarget.prototype.addEventListener = overwriteAddEvent as any;
    }
})();

Sentry.init({
    dsn: environment.sentryDSN,
    environment: environment.sentryEnvName,
    integrations: [
        // Registers and configures the Tracing integration,
        // which automatically instruments your application to monitor its
        // performance, including custom Angular routing instrumentation
        Sentry.browserTracingIntegration(),
        // Registers the Replay integration,
        // which automatically captures Session Replays
        Sentry.replayIntegration(),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    // We recommend adjusting this value in production
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});

// Which host this is decides both which product Plausible counts and which
// stale cookie has to go. Read before bootstrap, so it is a plain function
// rather than the injectable AppSurfaceService.
const surface = surfaceForHost(window.location.host, APP_ORIGINS);

const plausibleDomain =
    surface === 'briefings' ? environment.plausibleDomainBriefings : environment.plausibleDomain;

if (plausibleDomain) {
    plausibleInit({
        domain: plausibleDomain,
        endpoint: environment.plausibleEndpoint,
        outboundLinks: true,
        fileDownloads: true,
    });
}

// See legacy-cookie.ts: the auth cookie moved from a single host to the parent
// domain, and the old one has to be cleared before any request carries both.
// `hostOf` and not a bare `new URL`: this runs at module scope, before the
// DOMContentLoaded listener below is even registered, and the origins come from
// a gitignored environment file edited by hand on the deploy host. One empty
// string there would throw here and leave the app server-rendered but never
// bootstrapped -- on both hosts, silently, with no error a user can see.
dropLegacyHostCookie(
    document,
    environment.authCookieName,
    [APP_ORIGINS.estimator, APP_ORIGINS.briefings].map(hostOf).filter(host => !!host),
);

// One URL, one home. The bundle is served on two hosts, so the canonical
// address is whichever host is actually serving this path. The server bootstrap
// sets the same tag, because that is the copy a crawler reads first.
setCanonicalHref(document, originFor(surface, APP_ORIGINS), window.location.pathname);

document.addEventListener('DOMContentLoaded', () => {
    bootstrapApplication(CoreComponent, {
    providers: [
        provideZoneChangeDetection(), importProvidersFrom(CoreModule),
        {
            provide: CookieService,
            useClass: BrowserCookieService
        }
    ]
})
});
